import React, { useMemo } from 'react';
import { AppTable, AppPagination, AppTooltip, AppCheckbox } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import type { ColumnsType } from 'antd/es/table';
import type { MassagerCsvRow, MassagerCsvPagination } from '../types';

interface Props {
  data: MassagerCsvRow[];
  pagination: MassagerCsvPagination | null;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  hasSearched: boolean;
  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;
  isAllSelected: boolean;
  excludedIds: string[];
  onSelectAllChange: (checked: boolean) => void;
  onExcludedIdsChange: (ids: string[]) => void;
}

const MassagerCsvTable: React.FC<Props> = ({
  data,
  pagination,
  onPageChange,
  isLoading,
  hasSearched,
  selectedIds,
  onSelectIds,
  isAllSelected,
  excludedIds,
  onSelectAllChange,
  onExcludedIdsChange,
}) => {
  const isRowSelected = (id: string) => isAllSelected
    ? !excludedIds.includes(id)
    : selectedIds.includes(id);
  const currentPageSelectedCount = data.filter(row => isRowSelected(row.composite_id)).length;

  const columns: ColumnsType<MassagerCsvRow> = useMemo(
    () => [
      {
        title: (
          <div className="flex justify-center">
            <AppCheckbox
              checked={hasSearched && data.length > 0 && currentPageSelectedCount === data.length}
              indeterminate={
                currentPageSelectedCount > 0
                && data.length > 0
                && currentPageSelectedCount < data.length
              }
              disabled={!hasSearched || data.length === 0}
              onChange={e => {
                if (e.target.checked) {
                  onSelectAllChange(true);
                } else {
                  onSelectAllChange(false);
                  onSelectIds([]);
                }
              }}
            />
          </div>
        ),
        dataIndex: 'checkbox',
        key: 'checkbox',
        width: 56,
        align: 'center',
        render: (_: unknown, record: MassagerCsvRow) => (
          <div className="flex justify-center">
            <AppCheckbox
              checked={isRowSelected(record.composite_id)}
              onChange={e => {
                if (isAllSelected) {
                  if (e.target.checked) {
                    onExcludedIdsChange(excludedIds.filter(id => id !== record.composite_id));
                  } else if (!excludedIds.includes(record.composite_id)) {
                    onExcludedIdsChange([...excludedIds, record.composite_id]);
                  }
                  return;
                }
                if (e.target.checked) {
                  onSelectIds([...selectedIds, record.composite_id]);
                } else {
                  onSelectIds(selectedIds.filter(id => id !== record.composite_id));
                }
              }}
            />
          </div>
        ),
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 120,
        render: (val: string | number | null | undefined, record: MassagerCsvRow) => (
          <PatientEditLink patientId={record.patient_id} value={val} />
        ),
      },
      {
        title: '受診者名',
        dataIndex: 'patient_name',
        key: 'patient_name',
        width: 160,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '保険者番号',
        dataIndex: 'insurer_number',
        key: 'insurer_number',
        width: 120,
      },
      {
        title: '保険者名',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 300,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '施術師名称',
        dataIndex: 'massager_name',
        key: 'massager_name',
        width: 220,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
    ],
    [
      data,
      selectedIds,
      isAllSelected,
      excludedIds,
      currentPageSelectedCount,
      onSelectAllChange,
      onExcludedIdsChange,
      onSelectIds,
    ]
  );

  return (
    <div>
      <AppTable
        columns={columns}
        dataSource={data}
        rowKey="composite_id"
        recordCount={hasSearched ? (pagination?.total ?? data.length) : undefined}
        loading={isLoading}
        pagination={false}
        hasFilters={hasSearched}
        tableLayout="fixed"
        rowClassName={(record) => isRowSelected(record.composite_id) ? 'bg-blue-50' : ''}
      />

      {hasSearched && pagination && pagination.last_page > 1 && (
        <div className="mt-2 flex items-center justify-between gap-3">
          <div />
          <AppPagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default MassagerCsvTable;
